import { flow, pipe } from 'fp-ts/lib/function';
import {
  addVersionForUpdate,
  createAuthorizedGetRequest,
  createAuthorizedPutRequest,
  decodeVersioned,
  decode,
  fetchApi,
  setBody,
  Versioned,
} from '@/services/api';
import { chain, map } from 'fp-ts/TaskEither';
import { Organization } from '@/entities/organization';
import {
  organizationApiKeyResponseCodec,
  organizationCodec,
  organizationSecretApiKeyResponseCodec,
  organizationLanguagesCodec,
} from '@/services/api/resources/organization/codec';

const baseUrl = '/v2/organizations';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;
const firstLoginViewedUrl = (id: number): string => `${recordUrl(id)}/first-login-viewed`;
const organizationLanguagesUrl = (id: number): string => `${recordUrl(id)}/languages`;

export const updateOrganization = (id: number, organization: Versioned<Organization>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(organization.version)),
  map(setBody(organization.data)),
  chain(fetchApi),
);

export const setFirstLoginViewed = (id: number) => pipe(
  id,
  firstLoginViewedUrl,
  createAuthorizedPutRequest,
  chain(fetchApi),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(organizationCodec)),
);

export const getLanguages = flow(
  organizationLanguagesUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(organizationLanguagesCodec)),
);

export const getApiKey = flow(
  (id: number) => `${recordUrl(id)}/api-keys`,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(organizationApiKeyResponseCodec)),
);

export const getSecretApiKey = flow(
  (id: number) => `${recordUrl(id)}/api-keys/secret`,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(organizationSecretApiKeyResponseCodec)),
);

export const resetApiKeys = flow(
  (id: number) => `${recordUrl(id)}/api-keys`,
  createAuthorizedPutRequest,
  chain(fetchApi),
);
